import { useMemo } from "react";
import Supercluster from "supercluster";

/**
 * Custom hook to manage marker clustering using Supercluster
 * @param {Array} markers - Array of marker objects with id, lat, lng, name
 * @param {Object} viewState - Current map view state with zoom, latitude, longitude
 * @param {Object} mapRef - Reference to the map instance
 * @returns {Array} Array of clusters and individual markers to render
 */
export function useMarkerClusters(markers, viewState, mapRef) {
  // Initialize supercluster with configuration
  const supercluster = useMemo(() => {
    const cluster = new Supercluster({
      radius: 40, // Cluster radius in pixels
      maxZoom: 20, // Max zoom to cluster points on
      minZoom: 4, // Min zoom to cluster points on
      minPoints: 4, // Minimum points to form a cluster
    });

    // Convert markers to GeoJSON format required by supercluster
    const points = markers.map((marker) => ({
      type: "Feature",
      properties: {
        cluster: false,
        markerId: marker.id,
        name: marker.name,
      },
      geometry: {
        type: "Point",
        coordinates: [marker.lng, marker.lat],
      },
    }));

    cluster.load(points);
    return cluster;
  }, [markers]);

  // Get clusters for current map bounds and zoom
  const clusters = useMemo(() => {
    // If map is not ready, use viewState to calculate approximate bounds
    if (!mapRef.current) {
      // Calculate approximate bounds based on viewState
      // This provides initial clusters before the map fully loads
      const { longitude, latitude, zoom } = viewState;

      // Approximate bounds calculation (rough estimate)
      const latOffset = 180 / Math.pow(2, zoom);
      const lngOffset = 360 / Math.pow(2, zoom);

      return supercluster.getClusters(
        [
          longitude - lngOffset,
          latitude - latOffset,
          longitude + lngOffset,
          latitude + latOffset
        ],
        Math.floor(zoom)
      );
    }

    const map = mapRef.current.getMap();
    const bounds = map.getBounds();

    return supercluster.getClusters(
      [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
      Math.floor(viewState.zoom)
    );
  }, [supercluster, viewState, mapRef]);

  return { clusters, supercluster };
}

